import React, { useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { UserPlus, Wallet, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { AuthErrorMessage } from '../components/auth/AuthErrorMessage';
import { AuthPageShell } from '../components/auth/AuthPageShell';
import { AuthWalletHint } from '../components/auth/AuthWalletHint';
import { useAuth } from '../context/AuthContext';
import { useRedirectIfAuthenticated } from '../hooks/useRedirectIfAuthenticated';
import { AUTH_ACTIONS } from '../services/authService';
import {
  isValidUsername,
  sanitizeUsername,
  USERNAME_RULE_MESSAGE,
} from '../utils/username';

function sanitizeReferralCode(value) {
  return value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase().slice(0, 16);
}

function isValidReferralCode(value) {
  return !value || /^[A-Z0-9]{3,16}$/.test(value);
}

export function RegisterPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { register, isConnecting, connectingAction, error, clearError } =
    useAuth();
  const [username, setUsername] = useState('');
  const [referralCode, setReferralCode] = useState(() =>
    sanitizeReferralCode(searchParams.get('ref') || '')
  );
  const [message, setMessage] = useState('');

  useRedirectIfAuthenticated();

  const trimmedUsername = username.trim();
  const canSubmit =
    isValidUsername(trimmedUsername) &&
    isValidReferralCode(referralCode) &&
    !isConnecting;

  const handleSubmit = () => {
    clearError();
    if (!isValidUsername(trimmedUsername)) {
      setMessage(USERNAME_RULE_MESSAGE);
      return;
    }
    if (!isValidReferralCode(referralCode)) {
      setMessage('Referral code ต้องเป็น A-Z หรือ 0-9 จำนวน 3-16 ตัว');
      return;
    }

    setMessage('');
    register(trimmedUsername, referralCode || undefined)
      .then(() => navigate('/', { replace: true }))
      .catch(() => {});
  };

  const isRegistering =
    isConnecting && connectingAction === AUTH_ACTIONS.REGISTER;

  return (
    <AuthPageShell>
      <Card className="border-2 border-brand-gold/40 shadow-xl">
        <CardHeader className="bg-brand-gold-light/20">
          <CardTitle className="flex items-center gap-2 text-brand-red">
            <UserPlus className="h-5 w-5" />
            สมัครสมาชิก
          </CardTitle>
          <p className="mt-1 text-sm text-gray-600">
            ตั้ง username แล้วเชื่อม Web3 wallet เพื่อลงทะเบียน
          </p>
        </CardHeader>
        <CardContent className="space-y-5">
          <div>
            <label
              htmlFor="register-username"
              className="mb-1 block text-sm font-medium text-gray-700"
            >
              Username
            </label>
            <input
              id="register-username"
              type="text"
              value={username}
              onChange={(event) => setUsername(sanitizeUsername(event.target.value))}
              placeholder="เช่น mungkorn_player"
              maxLength={20}
              autoComplete="username"
              className="w-full rounded-lg border-2 border-gray-200 px-4 py-3 focus:border-brand-red focus:outline-none"
            />
            <p className="mt-1 text-xs text-gray-500">
              3–20 ตัวอักษร: a-z, A-Z, 0-9, _
            </p>
          </div>

          <div>
            <label
              htmlFor="register-referral"
              className="mb-1 block text-sm font-medium text-gray-700"
            >
              Referral code (ถ้ามี)
            </label>
            <input
              id="register-referral"
              type="text"
              value={referralCode}
              onChange={(event) =>
                setReferralCode(sanitizeReferralCode(event.target.value))
              }
              placeholder="เช่น MG000001"
              maxLength={16}
              autoComplete="off"
              className="w-full rounded-lg border-2 border-gray-200 px-4 py-3 uppercase focus:border-brand-red focus:outline-none"
            />
            <p className="mt-1 text-xs text-gray-500">
              ใส่ code ผู้แนะนำเพื่อให้ผู้แนะนำได้รับ 3% เมื่อคุณฝาก USDT สำเร็จ
            </p>
          </div>

          <AuthWalletHint title="ขั้นตอนถัดไป">
            กดปุ่มด้านล่างเพื่อเชื่อม MetaMask และลงชื่อยืนยัน wallet
          </AuthWalletHint>

          <AuthErrorMessage message={message || error} />

          <Button
            size="lg"
            className="w-full"
            disabled={!canSubmit}
            onClick={handleSubmit}
          >
            {isRegistering ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                กำลังสมัคร...
              </>
            ) : (
              <>
                <Wallet className="h-4 w-4" />
                สมัครด้วย Web3 Wallet
              </>
            )}
          </Button>

          <p className="text-center text-sm text-gray-600">
            มีบัญชีแล้ว?{' '}
            <Link
              to="/login"
              className="font-semibold text-brand-red hover:underline"
            >
              เข้าสู่ระบบ
            </Link>
          </p>
        </CardContent>
      </Card>
    </AuthPageShell>
  );
}
